import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { TriangleLogo } from "@/components/TriangleLogo";
import { BetaBadge } from "@/components/shared/BetaBadge";
import {
  SquaresFour,
  FileText,
  UserFocus,
  ChatCircle,
  VideoCamera,
  ChartLineUp,
  Funnel,
  Gear,
  User,
  Users,
  Question,
  CaretLeft,
  CaretRight,
  SealCheck,
  ShieldCheck,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useUIStore } from "@/stores/uiStore";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { SidebarOnboardingItem } from "@/components/onboarding/SidebarOnboardingItem";

const menuItems = [
  { title: "Dashboard", url: "/", icon: SquaresFour },
  { title: "Roteiros", url: "/roteiros", icon: FileText },
  { title: "Perfil do Cliente", url: "/decodificar", icon: UserFocus },
  { title: "Simulador", url: "/simulador", icon: ChatCircle },
  { title: "Analisar Criativo", url: "/criativos", icon: VideoCamera },
  { title: "Analisar Consulta", url: "/consultas", icon: ChartLineUp },
  // { title: "Funil de Vendas", url: "/funil", icon: Funnel }, // hidden: not implemented
  { title: "Raio-X do Funil", url: "/raio-x", icon: Funnel },
  // { title: "Meu Instagram", url: "/instagram", icon: ChartBar }, // hidden: not implemented
  // { title: "Ranking", url: "/ranking", icon: Trophy }, // hidden: not implemented yet
];

const bottomItems = [
  { title: "Configurações", url: "/configuracoes", icon: Gear },
  { title: "Perfil", url: "/perfil", icon: User },
  { title: "Ajuda", url: "/ajuda", icon: Question },
  { title: "Privacidade e termos", url: "/legal/privacidade", icon: ShieldCheck },
];

const adminItems = [{ title: "Admin", url: "/hub", icon: Users }];

interface AppSidebarProps {
  isAdmin?: boolean;
  variant?: "persistent" | "drawer";
}

export function AppSidebar({ isAdmin = false, variant = "persistent" }: AppSidebarProps) {
  const location = useLocation();
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const setCollapsed = useUIStore((s) => s.setSidebarCollapsed);
  const setDrawerOpen = useUIStore((s) => s.setDrawerOpen);

  const isDrawer = variant === "drawer";
  const effectiveCollapsed = isDrawer ? false : collapsed;
  const { isComplete: profileVerified } = useBusinessContext();

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleNavClick = () => {
    if (isDrawer) setDrawerOpen(false);
  };

  const NavItem = ({
    item,
    showTooltip = false,
  }: {
    item: (typeof menuItems)[number];
    showTooltip?: boolean;
  }) => {
    const active = isActive(item.url);

    const content = (
      <NavLink
        to={item.url}
        onClick={handleNavClick}
        aria-current={active ? "page" : undefined}
        aria-label={effectiveCollapsed ? item.title : undefined}
        className={cn(
          "group relative flex items-center gap-3 px-3 rounded-lg overflow-hidden",
          isDrawer ? "h-12" : "h-12 lg:h-10",
          "transition-[background,color,box-shadow] duration-300 ease-out",
          // v3: 3px left rail + subtle fill (Notion/Linear-style) — no inset ring
          active
            ? "bg-primary/[0.06] text-foreground"
            : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
        )}
      >
        {/* Left accent — fades + scales in via CSS only */}
        <span
          aria-hidden
          className={cn(
            "absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-primary origin-center",
            "transition-all duration-300 ease-out",
            active ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0",
          )}
        />
        <item.icon
          weight={active ? "fill" : "regular"}
          aria-hidden="true"
          className={cn(
            "w-5 h-5 flex-shrink-0 transition-colors duration-200",
            active ? "text-primary" : "text-muted-foreground group-hover:text-foreground",
          )}
        />
        {!effectiveCollapsed && (
          <span className="text-sm font-medium truncate flex-1">{item.title}</span>
        )}
        {!effectiveCollapsed && "badge" in item && (item as { badge?: string }).badge && (
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary font-medium">
            {(item as { badge?: string }).badge}
          </span>
        )}
        {item.url === "/perfil" && profileVerified ? (
          <SealCheck
            weight="fill"
            className={cn(
              "text-primary flex-shrink-0",
              effectiveCollapsed
                ? "absolute -top-0.5 -right-0.5 w-3 h-3 bg-background rounded-full"
                : "w-4 h-4",
            )}
            aria-label="Perfil verificado"
          />
        ) : null}
      </NavLink>
    );

    if (effectiveCollapsed && showTooltip) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="bg-card border-border">
            {item.title}
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <aside
      aria-label="Navegação principal"
      className={cn(
        "relative flex flex-col bg-sidebar transition-all duration-300",
        isDrawer ? "h-full w-full" : cn("h-full", effectiveCollapsed ? "w-16" : "w-60"),
      )}
    >
      <div className="flex items-center justify-between p-5">
        <div
          className={cn("flex items-center gap-3", effectiveCollapsed && "justify-center w-full")}
        >
          <TriangleLogo size="sm" animate />
          {!effectiveCollapsed && (
            <div className="flex flex-col leading-tight">
              <span className="text-[11px] font-semibold text-foreground tracking-[0.18em]">
                CÓDIGO DO
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-semibold text-primary tracking-[0.18em]">
                  PODER
                </span>
                <BetaBadge />
              </div>
            </div>
          )}
        </div>
      </div>

      {!isDrawer && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expandir menu lateral" : "Recolher menu lateral"}
          aria-expanded={!collapsed}
          className="absolute -right-3 top-6 w-6 h-6 rounded-full border border-border bg-card hover:bg-accent z-10"
        >
          {collapsed ? (
            <CaretRight weight="bold" aria-hidden="true" className="w-3 h-3" />
          ) : (
            <CaretLeft weight="bold" aria-hidden="true" className="w-3 h-3" />
          )}
        </Button>
      )}

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {menuItems.map((item) => (
          <NavItem key={item.url} item={item} showTooltip />
        ))}

        {isAdmin && (
          <>
            <div className="my-4 border-t border-sidebar-border" />
            {adminItems.map((item) => (
              <NavItem key={item.url} item={item} showTooltip />
            ))}
          </>
        )}
      </nav>

      <div className="p-3 space-y-0.5">
        <SidebarOnboardingItem
          collapsed={effectiveCollapsed}
          isDrawer={isDrawer}
          onNavigate={handleNavClick}
        />
        {bottomItems.map((item) => (
          <NavItem key={item.url} item={item} showTooltip />
        ))}
      </div>
    </aside>
  );
}
